import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { MRT_ColumnDef } from 'material-react-table'
import { TFunction } from 'i18next'
import { AlertTypeOption } from '../../../../hooks/useAlertTypeOptions'
import { UserData } from '../../../../redux/slices/userSlice'
import {
    getSeverityColor,
    getStatusIconPath,
    stripTimeFromMessage,
} from '../../../../utils/AlertUtils'
import { formatDateTimeWithUserPreferences } from '../../../../utils/DateAndTimeUtils'
import EntityTableHeader from '../../../Common/EntityTableHeader'
import { Alert } from './interfaces'

type BuildAlertsColumnsParams = {
    t: TFunction
    showStatus: boolean
    user: UserData | null
    canWrite: boolean
    updateStatus: (alert: Alert, newStatus: number) => void
    handleStatusMenuClick: (
        e: React.MouseEvent<HTMLElement>,
        alert: Alert,
    ) => void
    alertTypeOptions: AlertTypeOption[]
}

/**
 * Builds the Alerts table's MRT column definitions. Pure factory - callers
 * wrap this in their own `useMemo`.
 */
export const buildAlertsColumns = ({
    t,
    showStatus,
    user,
    canWrite,
    updateStatus,
    handleStatusMenuClick,
    alertTypeOptions,
}: BuildAlertsColumnsParams): MRT_ColumnDef<Alert>[] => {
    const baseCols: MRT_ColumnDef<Alert>[] = [
        {
            accessorKey: 'severity',
            header: t('severity'),
            Header: (d) => (
                <EntityTableHeader text={d.column.columnDef.header as string} />
            ),
            size: 120,
            Cell: ({ row }) => (
                <Typography
                    sx={{
                        color: getSeverityColor(row.original.severity),
                        fontSize: '14px',
                        fontWeight: 500,
                    }}
                >
                    {row.original.severity}
                </Typography>
            ),
        },
        {
            accessorKey: 'message',
            header: t('Message'),
            Header: (d) => (
                <EntityTableHeader text={d.column.columnDef.header as string} />
            ),
            size: 500,
            minSize: 350,
            maxSize: 600,
            Cell: ({ cell, row }) => {
                const cleanMessage = stripTimeFromMessage(
                    cell.getValue<string>(),
                )
                const alertType = row.original.alertType
                const alertTypeTitle = alertTypeOptions.find(
                    (e) => e.id == alertType,
                )

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            width: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <Typography
                            fontWeight="500"
                            sx={{
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {alertTypeTitle?.name || t('Unknown Alert')}
                        </Typography>
                        <Typography
                            sx={{
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {cleanMessage}
                        </Typography>
                    </Box>
                )
            },
        },
        {
            accessorKey: 'time',
            header: t('Time'),
            size: 180,
            minSize: 180,
            maxSize: 180,
            Header: (d) => (
                <EntityTableHeader text={d.column.columnDef.header as string} />
            ),
            Cell: ({ row }) => (
                <Typography
                    fontSize="14px"
                    sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                    }}
                >
                    {formatDateTimeWithUserPreferences(
                        row.original.time,
                        user?.preferences?.localization,
                    )}
                </Typography>
            ),
        },
    ]

    if (showStatus) {
        baseCols.push({
            accessorKey: 'status',
            header: t('Status'),
            Header: (d) => (
                <EntityTableHeader text={d.column.columnDef.header as string} />
            ),
            size: 100,
            Cell: ({ row }) => {
                const statusValue = row.original.status
                const iconPath = getStatusIconPath(
                    statusValue,
                    row.original.severity,
                )

                // Toggle Logic: If 1 (Read) -> set to 0 (Unread). Otherwise -> set to 1 (Read).
                const handleIconClick = (e: React.MouseEvent) => {
                    if (!canWrite) return
                    e.stopPropagation()
                    const nextStatus = statusValue === 1 ? 0 : 1
                    updateStatus(row.original, nextStatus)
                }

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        <Tooltip
                            title={
                                statusValue === 1
                                    ? t('Mark as Unread')
                                    : t('Mark as Read')
                            }
                        >
                            <Box
                                id={`alert-status-toggle-icon-row-${row.index}`}
                                component="img"
                                src={iconPath}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    cursor: canWrite ? 'pointer' : 'default',
                                    opacity: canWrite ? 1 : 0.7,
                                    transition: 'transform 0.1s active',
                                    '&:active': {
                                        transform: 'scale(0.95)',
                                    },
                                }}
                                onClick={handleIconClick}
                            />
                        </Tooltip>

                        {canWrite && (
                            <IconButton
                                id={`alert-status-menu-button-row-${row.index}`}
                                size="small"
                                onClick={(e) =>
                                    handleStatusMenuClick(e, row.original)
                                }
                                sx={{ p: 0 }}
                            >
                                <ExpandMoreIcon
                                    sx={{ fontSize: 18, color: '#808080' }}
                                />
                            </IconButton>
                        )}
                    </Box>
                )
            },
        })
    }
    return baseCols
}
